import {
    ServiceUtils as AndcultureCodeServiceUtils,
    PromiseFactory,
    ResultRecord,
} from "@rsm-hcd/javascript-core";
import { AxiosResponse } from "axios";
import GlobalStateRecord from "models/view-models/global-state-record";
import { GlobalStateUpdater } from "./contexts/use-global-state-context";
import UserConfigurationErrorKeys from "constants/user-configuration-error-keys";
import { siteMap } from "internal-sitemap";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ServicesConfiguration {
    cultureCode?: string;
    globalState: GlobalStateRecord;
    setGlobalState: GlobalStateUpdater;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Variables
// -----------------------------------------------------------------------------------------

let _configuration: ServicesConfiguration;
let triggerUnauthorizedStatus: (() => void) | undefined;

// #endregion Variables

// -----------------------------------------------------------------------------------------
// #region Types
// -----------------------------------------------------------------------------------------

type UserConfigurationErrorKey =
    (typeof UserConfigurationErrorKeys)[keyof typeof UserConfigurationErrorKeys];

// #endregion Types

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

/**
 * Configure services globally
 */
const configure = (configuration: ServicesConfiguration) => {
    _configuration = configuration;

    AndcultureCodeServiceUtils.configure(
        _configuration.cultureCode,
        handleApiResponseError,
        handleApiResponseSuccess
    );
};

const isValidUserConfigErrorKey = (
    key: string | undefined
): key is UserConfigurationErrorKey => {
    return Object.values(UserConfigurationErrorKeys).includes(key as any);
};

const setUnauthorizedStatusHandler = (updater: () => void) => {
    triggerUnauthorizedStatus = updater;
};

// #endregion Functions

// -----------------------------------------------------------------------------------------
// #region Event Handlers
// -----------------------------------------------------------------------------------------

const handleApiResponseError = (error: any) => {
    if (error == null || error.response == null) {
        return Promise.reject(error);
    }

    const response = error.response;

    // Handle global HTTP status codes
    switch (response.status) {
        case 401: {
            _configuration.setGlobalState(
                _configuration.globalState.setUnauthenticated()
            );

            if (typeof triggerUnauthorizedStatus === "function") {
                triggerUnauthorizedStatus();
            } else {
                // If the handler isn't set then the Auth Provider didn't work. This probably won't
                // happen, but if it does, just go to the landing page to make sure we're not stuck on the loading screen
                window.location.href = siteMap.home;
            }
            return PromiseFactory.pending(); // Short-circuit promise chain
        }
        case 333:
        case 403: {
            // Try to instantiate a new ResultRecord from the response data, if it is an actual object
            let resultRecord = new ResultRecord().addError("Unknown", error);
            if (response.data != null && typeof response.data === "object") {
                resultRecord = new ResultRecord(response.data);
            }

            if (resultRecord.hasErrors()) {
                if (
                    resultRecord.errors != null &&
                    resultRecord.errors.length > 0
                ) {
                    //look for any that contain UserConfiguration.
                    const hasUserConfigurationError = resultRecord.errors.some(
                        (err) => isValidUserConfigErrorKey(err.key)
                    );

                    if (hasUserConfigurationError) {
                        _configuration.setGlobalState(
                            (globalState: GlobalStateRecord) =>
                                globalState.with({
                                    unauthorizedResult: resultRecord,
                                })
                        );
                        return PromiseFactory.pending(); // Short-circuit promise chain
                    }
                }
            }
            return Promise.reject(resultRecord);
        }
        case 590: {
            _configuration.setGlobalState((globalState: GlobalStateRecord) =>
                globalState.with({
                    isAppInMaintenanceMode: true,
                })
            );
            return PromiseFactory.pending(); // Short-circuit promise chain
        }
    }

    // If shape of a Result, directly wrap in result record
    const data = response.data;
    if (data != null && data.errors != null) {
        return Promise.reject(new ResultRecord(data));
    }

    // Wrap unhandled error in result record
    let unhandledResult = new ResultRecord();
    unhandledResult = unhandledResult.addError("Unknown", error);
    return Promise.reject(unhandledResult);
};

const handleApiResponseSuccess = (response: AxiosResponse) => response;

// #endregion Event Handlers

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

const ServiceUtils = {
    configure,
    handleApiResponseError,
    handleApiResponseSuccess,
    mapAxiosResponse: AndcultureCodeServiceUtils.mapAxiosResponse,
    mapPagedAxiosResponse: AndcultureCodeServiceUtils.mapPagedAxiosResponse,
    setUnauthorizedStatusHandler,
};

export default ServiceUtils;

// #endregion Exports
